import { Component, OnInit } from '@angular/core';
import { BentoAlertItemOptions } from '@bento/bento-ng';
import { TranslateService } from '@ngx-translate/core';
import { clientsRequired, staffRequired, setClientsRequired, setStaffRequired } from '@onviobr/services/mfa';
import { getBindings } from '@onviobr/services/session';
import { getPasswordExpiration, updatePasswordExpiration } from './flexible-password-expiration';
import { has as hasAccess } from '@onviobr/services/access';

@Component({
	selector: 'app-settings-security',
	templateUrl: './setting-security.component.html',
	styleUrls: ['./setting-security.component.css']
})
export class SettingSecurityComponent implements OnInit {
	static defaultPwdExpirationDays = 90;
	alerts: BentoAlertItemOptions[] = [];
	isLoading = true;
	isSaving = false;
	isFlexiblePassword = true;
	pwdExpirationDays = SettingSecurityComponent.defaultPwdExpirationDays;

	public getBindings = getBindings;
	public hasAccess = hasAccess;
	public mfa = {
		staffRequired,
		clientsRequired,
		setStaffRequired,
		setClientsRequired
	};
	public flexiblePasswordExpiration = {
		updatePasswordExpiration,
		getPasswordExpiration
	};

	companyId: string;
	clientMfaRequired: boolean;
	staffMfaRequired: boolean;
	staffMfaRequiredOverrideIac: boolean;

	previousVal: {
		staffMFA?: boolean;
		clientMFA?: boolean;
		pwdExpirationDays?: number;
	};

	constructor(
		public translate: TranslateService
	) {}

	async ngOnInit() {
		this.isLoading = true;
		this.previousVal = {};
		try {
			this.isFlexiblePassword = !(await this.hasAccess('Platform_FlexPasswordExpireDisallow'));
			const bindings = await this.getBindings();
			this.companyId = bindings.ApplicationConfiguration.CompanyId;
			await Promise.all([
				this.getMFASettings(),
				this.getPwdExpirationSetting()
			]);
		} catch (error) {
			console.error(error);
		}
		this.isLoading = false;
	}

	public async getMFASettings() {
		this.clientMfaRequired = await this.mfa.clientsRequired(this.companyId);
		this.staffMfaRequired = await this.mfa.staffRequired(this.companyId);
		// disallow modifying mfa requirement for staff if RequireStaffMFA iac is on
		this.staffMfaRequiredOverrideIac = await this.hasAccess('Platform_RequireStaffMFA');
		this.setPreviousMFA(this.clientMfaRequired, this.staffMfaRequired);
	}

	public async getPwdExpirationSetting() {
		if (this.isFlexiblePassword) {
			this.pwdExpirationDays = await this.flexiblePasswordExpiration.getPasswordExpiration();
		}
		this.setPreviousExpirationDays(this.pwdExpirationDays);
	}

	async save() {
		this.isSaving = true;
		try {
			await this.mfa.setClientsRequired(this.companyId, this.clientMfaRequired);
			await this.mfa.setStaffRequired(this.companyId, this.staffMfaRequired);
			if (this.isFlexiblePassword) {
				await this.flexiblePasswordExpiration.updatePasswordExpiration(this.pwdExpirationDays);
			}
			this.translate.get('Onvio.Setup.Security.Success').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'success',
					closeable: true
				});
			});
		} catch {
			this.translate.get('Onvio.Setup.Security.Error').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'danger',
					closeable: true
				});
			});
		}
		this.isSaving = false;
	}

	cancel() {
		this.staffMfaRequired = this.previousVal.staffMFA;
		this.clientMfaRequired = this.previousVal.clientMFA;
		this.pwdExpirationDays = this.previousVal.pwdExpirationDays;
	}

	setPreviousMFA(clientMfa: boolean, staffMfa: boolean) {
		this.previousVal.clientMFA = clientMfa;
		this.previousVal.staffMFA = staffMfa;
	}

	setPreviousExpirationDays(expirationDays: number) {
		this.previousVal.pwdExpirationDays = expirationDays;
	}
}
